import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Simple mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

// Card animation variants matching dashboard design - mobile optimized
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// Enhanced floating animations - disabled on mobile
const floatingVariants = {
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

// Mobile-optimized motion component
const MobileOptimizedMotion = ({ children, className, variants, whileHover, transition, initial, animate, exit, ...props }: any) => {
  const isMobile = useIsMobile();
  
  if (isMobile) {
    // Return a regular div on mobile
    return <div className={className}>{children}</div>;
  }
  // Return motion.div on desktop
  return (
    <motion.div 
      className={className}
      variants={variants}
      initial={initial}
      animate={animate}
      exit={exit}
      whileHover={whileHover}
      transition={transition}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Token configuration with proper icons
const TOKEN_CONFIG = {
  XRP: {
    name: 'XRP',
    symbol: 'XRP',
    icon: '/images/XRP.png',
    decimals: 6,
    color: 'blue'
  },
  RLUSD: {
    name: 'RLUSD',
    symbol: 'RLUSD',
    icon: '/images/RLUSD.png',
    decimals: 6,
    color: 'blue'
  }
};

// Chain configuration with proper icons
const CHAIN_CONFIG = {
  XRPL: {
    name: 'XRP Ledger',
    symbol: 'XRPL',
    icon: '/icons/partners/ripple.svg',
    color: 'blue'
  },
  Ethereum: {
    name: 'Ethereum',
    symbol: 'ETH',
    icon: '/icons/partners/ethereum.svg',
    color: 'purple'
  },
  Arbitrum: {
    name: 'Arbitrum',
    symbol: 'ARB',
    icon: '/icons/partners/arbitrum.svg',
    color: 'blue'
  },
  BNB: {
    name: 'BNB Smart Chain',
    symbol: 'BNB',
    icon: '/icons/partners/BNB.svg',
    color: 'yellow'
  }
};

// Type definitions
type TokenConfigType = typeof TOKEN_CONFIG;
type ChainConfigType = typeof CHAIN_CONFIG;
type ConfigType = {
  name: string;
  symbol: string;
  icon: string;
  color: string;
  decimals?: number;
};

// Enhanced Token/Chain Selection Modal
const SelectionModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  options,
  title = "Select Option",
  type = "token"
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onSelect: (option: string) => void;
  options: string[];
  title?: string;
  type?: 'token' | 'chain';
}) => {
  const isMobile = useIsMobile();
  
  if (!isOpen) return null;

  const config: TokenConfigType | ChainConfigType = type === 'token' ? TOKEN_CONFIG : CHAIN_CONFIG;

  const modalContent = (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-slate-900/80 backdrop-blur-xl flex items-center justify-center z-[9999] p-4">
      <motion.div 
        className="rounded-xl p-5 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 w-full max-w-sm"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3 }}
        whileHover={{ 
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        {/* Animated background pattern - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''} rounded-xl`}></div>
        
        <div className="relative z-10">
          {/* Modal Header */}
          <div className="flex justify-between items-center mb-5">
            <h3 className="text-xl font-semibold text-white">{title}</h3>
            <button 
              onClick={onClose} 
              className="p-1.5 rounded-lg hover:bg-slate-800/50 transition-colors text-slate-400 hover:text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Options List */}
          <div className="space-y-3">
            {options.map((optionKey, index) => {
              const option: ConfigType = config[optionKey as keyof typeof config] as ConfigType;
              return (
                <motion.button
                  key={optionKey}
                  onClick={() => {
                    onSelect(optionKey);
                    onClose();
                  }}
                  className="w-full p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-blue-500/40 transition-all duration-300 flex items-center gap-4 group"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ 
                    scale: 1.02,
                    boxShadow: "0 0 20px rgba(59, 130, 246, 0.1)"
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="relative">
                    <motion.div
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/40 flex items-center justify-center"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <img src={option.icon} alt={option.symbol} className="w-6 h-6" />
                    </motion.div>
                    <motion.div
                      className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full opacity-0 group-hover:opacity-100"
                      initial={{ scale: 0 }}
                      whileHover={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    />
                  </div>
                  <div className="text-left flex-1">
                    <div className="font-semibold text-white text-base">{option.symbol}</div>
                    <div className="text-sm text-slate-400">{option.name}</div>
                  </div>
                  <motion.svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={2} 
                    stroke="currentColor" 
                    className="w-5 h-5 text-slate-400 group-hover:text-blue-400 transition-colors"
                    whileHover={{ x: 5 }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </motion.svg>
                </motion.button>
              );
            })}
          </div>
        </div>
      </motion.div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

// Bridge direction type
type BridgeDirection = 'from-xrpl' | 'to-xrpl';
type ChainType = 'XRPL' | 'Ethereum' | 'Arbitrum' | 'BNB';
type AssetType = 'XRP' | 'RLUSD';

const Bridge: React.FC = () => {
  const isMobile = useIsMobile();
  const [direction, setDirection] = useState<BridgeDirection>('from-xrpl');
  const [asset, setAsset] = useState<AssetType>('XRP');
  const [sourceChain, setSourceChain] = useState<ChainType>('XRPL');
  const [targetChain, setTargetChain] = useState<ChainType>('Ethereum');
  const [amount, setAmount] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [showChainSelect, setShowChainSelect] = useState<'source' | 'target' | null>(null);
  const [showAssetSelect, setShowAssetSelect] = useState<boolean>(false);
  const [txStatus, setTxStatus] = useState<'idle' | 'bridging' | 'completed' | 'failed'>('idle');

  // Mock fee data
  const chainFees = {
    Ethereum: { XRP: 0.05, RLUSD: 2.5 },
    Arbitrum: { XRP: 0.02, RLUSD: 1.0 },
    BNB: { XRP: 0.01, RLUSD: 0.5 },
    XRPL: { XRP: 0.000012, RLUSD: 0.000012 }
  };

  // Mock balances
  const balances = {
    XRPL: { XRP: 1250, RLUSD: 5000 },
    Ethereum: { XRP: 100, RLUSD: 500 },
    Arbitrum: { XRP: 50, RLUSD: 250 },
    BNB: { XRP: 25, RLUSD: 100 }
  };

  const handleDirectionSwap = () => {
    if (direction === 'from-xrpl') {
      setDirection('to-xrpl');
      setSourceChain(targetChain);
      setTargetChain('XRPL');
    } else {
      setDirection('from-xrpl');
      setSourceChain('XRPL');
      setTargetChain(targetChain === 'XRPL' ? 'Ethereum' : targetChain);
    }
  };

  const handleBridge = () => {
    if (!amount || parseFloat(amount) <= 0) return;
    setShowConfirmation(true);
  };

  const confirmBridge = () => {
    setShowConfirmation(false);
    setTxStatus('bridging');
    
    // Simulate bridging process
    setTimeout(() => {
      setTxStatus('completed');
    }, 3000);
  };

  const resetForm = () => {
    setAmount('');
    setTxStatus('idle');
  };

  const getCurrentFee = () => {
    const fees = direction === 'from-xrpl' ? chainFees[targetChain] : chainFees[sourceChain];
    return fees[asset];
  };

  const getAvailableBalance = () => {
    return balances[sourceChain][asset];
  };

  const getEstimatedReceive = () => {
    if (!amount) return 0;
    return Math.max(0, parseFloat(amount) - getCurrentFee());
  };

  const availableChains = direction === 'from-xrpl' 
    ? ['Ethereum', 'Arbitrum', 'BNB'] as ChainType[]
    : ['Ethereum', 'Arbitrum', 'BNB'] as ChainType[];

  const sourceChainConfig = CHAIN_CONFIG[sourceChain];
  const targetChainConfig = CHAIN_CONFIG[targetChain];
  const assetConfig = TOKEN_CONFIG[asset];

  return (
    <div className="space-y-6">
      {/* Header Section with Dashboard Theme - Entry animation disabled */}
      <MobileOptimizedMotion 
        className="relative overflow-hidden rounded-xl p-6 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
        whileHover={{ 
          scale: 1.01,
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        {/* Enhanced animated background pattern - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''}`}></div>
        
        {/* Floating particles - disabled on mobile */}
        <MobileOptimizedMotion className="absolute top-4 left-8 w-2 h-2 bg-blue-400 rounded-full opacity-30" variants={floatingVariants} animate="animate" />
        <MobileOptimizedMotion className="absolute top-12 right-12 w-1 h-1 bg-white rounded-full opacity-20" variants={floatingVariants} animate="animate" style={{ animationDelay: '1s' }} />
        <MobileOptimizedMotion className="absolute bottom-8 left-16 w-1.5 h-1.5 bg-blue-400 rounded-full opacity-25" variants={floatingVariants} animate="animate" style={{ animationDelay: '2s' }} />
        
        <div className="relative z-10">
          <h1 className="text-2xl font-semibold text-white tracking-tight mb-3">
            Cross-Chain <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Bridge</span>
          </h1>
          <p className="text-slate-300 mb-6 max-w-2xl">
            Transfer assets seamlessly across different blockchain networks with optimal routing and minimal fees
          </p>
          
          {/* Stats Grid with Dashboard Design */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[
              { 
                title: 'TOTAL BRIDGED', 
                value: '$847.2M', 
                subtitle: 'Lifetime volume',
                progress: 85
              },
              { 
                title: 'ACTIVE ROUTES', 
                value: '15', 
                subtitle: 'Live bridge pairs',
                progress: 60
              },
              { 
                title: 'AVG TIME', 
                value: '~3min', 
                subtitle: 'Bridge completion',
                progress: 25
              }
            ].map((item, i) => (
              <motion.div 
                key={i}
                className="rounded-xl p-4 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
                variants={cardVariants}
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
                }}
              >
                <div className="text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">{item.title}</div>
                <div className="text-2xl font-semibold text-white mb-1">{item.value}</div>
                <div className="text-sm text-slate-300 mb-3">{item.subtitle}</div>
                
                {/* Progress bar with dashboard styling */}
                <div className="h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
                  <motion.div 
                    className="bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 h-1.5 rounded-full shadow-lg shadow-blue-500/50"
                    initial={{ width: 0 }}
                    animate={{ width: `${item.progress}%` }}
                    transition={{ duration: 1.5, delay: 0.5 + i * 0.1 }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </MobileOptimizedMotion>

      {/* Enhanced Main Bridge Interface */}
      <motion.div 
        className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
        variants={cardVariants}
        whileHover={{ 
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        {/* Enhanced animated background - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''}`}></div>
        
        {/* Animated grid pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.3) 1px, transparent 1px)`,
            backgroundSize: '30px 30px'
          }} />
        </div>

        <div className="relative z-10 p-6">
          {/* Interface Title - Entry animation disabled */}
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold text-white mb-2">Bridge Interface</h2>
            <p className="text-sm text-slate-300">Transfer assets across blockchain networks</p>
          </div>

          {/* Enhanced Bridge Container */}
          <div className="max-w-md mx-auto">
            <motion.div 
              className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 space-y-3 relative"
              variants={cardVariants}
            >
              {/* Subtle glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* From Chain with enhanced animations */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">From</label>
                <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50 relative group">
                  {/* Hover glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Chain Selection */}
                  <div className="flex items-center justify-between mb-2 relative z-10">
                    <span className="text-xs text-slate-400">Network</span>
                    <motion.button
                      onClick={() => setShowChainSelect('source')}
                      className="flex items-center gap-1 bg-slate-700/50 hover:bg-slate-700/70 rounded-lg px-2 py-2.5 border border-slate-600/50 transition-all duration-300 group min-w-[70px] max-w-[110px] flex-shrink-0 min-h-[44px]"
                      whileHover={isMobile ? {} : { scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <motion.div
                        className="relative"
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      >
                        <img src={sourceChainConfig.icon} alt={sourceChainConfig.symbol} className="w-4 h-4" />
                      </motion.div>
                      <span className="text-sm font-medium text-white flex-1">{sourceChainConfig.symbol}</span>
                      <motion.svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        strokeWidth={2} 
                        stroke="currentColor" 
                        className="w-3 h-3 text-slate-400 group-hover:text-blue-400 transition-colors"
                        whileHover={{ y: 1 }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                      </motion.svg>
                    </motion.button>
                  </div>
                  
                  {/* Token & Amount */}
                  <div className="flex items-center justify-between mb-2 relative z-10">
                    <input
                      type="text"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="flex-1 bg-transparent text-xl font-semibold text-white placeholder-slate-400 border-none outline-none mr-3"
                    />
                    <motion.button
                      onClick={() => setShowAssetSelect(true)}
                      className="flex items-center gap-1 bg-slate-700/50 hover:bg-slate-700/70 rounded-lg px-2 py-2.5 border border-slate-600/50 transition-all duration-300 group min-w-[70px] max-w-[110px] flex-shrink-0 min-h-[44px]"
                      whileHover={isMobile ? {} : { scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <motion.div
                        className="relative"
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      >
                        <img src={assetConfig.icon} alt={assetConfig.symbol} className="w-4 h-4" />
                      </motion.div>
                      <span className="text-sm font-medium text-white flex-1">{assetConfig.symbol}</span>
                      <motion.svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        strokeWidth={2} 
                        stroke="currentColor" 
                        className="w-3 h-3 text-slate-400 group-hover:text-blue-400 transition-colors"
                        whileHover={{ y: 1 }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                      </motion.svg>
                    </motion.button>
                  </div>
                  
                  <div className="flex justify-between text-xs relative z-10">
                    <span className="text-slate-400">
                      Balance: {getAvailableBalance().toLocaleString()} {assetConfig.symbol}
                    </span>
                    <motion.button 
                      onClick={() => setAmount(getAvailableBalance().toString())}
                      className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Max
                    </motion.button>
                  </div>
                </div>
              </motion.div>

              {/* Enhanced Bridge Direction Button */}
              <div className="flex justify-center">
                <motion.button
                  onClick={handleDirectionSwap}
                  className="w-10 h-10 bg-gradient-to-br from-slate-700/50 to-slate-800/50 hover:from-blue-500/20 hover:to-blue-600/20 rounded-full flex items-center justify-center border border-slate-600/50 hover:border-blue-500/40 transition-all duration-300 group relative"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {/* Rotating background effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-blue-600/20 rounded-full opacity-0 group-hover:opacity-100"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  />
                  
                  <motion.svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={2} 
                    stroke="currentColor" 
                    className="w-4 h-4 text-blue-400 group-hover:text-blue-300 transition-colors duration-300 relative z-10"
                    whileHover={{ rotate: 180 }}
                    transition={{ duration: 0.3 }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                  </motion.svg>
                  
                  {/* Pulse effect */}
                  <motion.div
                    className="absolute inset-0 bg-blue-400/30 rounded-full"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.button>
              </div>

              {/* To Chain with enhanced animations */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">To</label>
                <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50 relative group">
                  {/* Hover glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Chain Selection */}
                  <div className="flex items-center justify-between mb-2 relative z-10">
                    <span className="text-xs text-slate-400">Network</span>
                    <motion.button
                      onClick={() => setShowChainSelect('target')}
                      className="flex items-center gap-1 bg-slate-700/50 hover:bg-slate-700/70 rounded-lg px-2 py-2.5 border border-slate-600/50 transition-all duration-300 group min-w-[70px] max-w-[110px] flex-shrink-0 min-h-[44px]"
                      whileHover={isMobile ? {} : { scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <motion.div
                        className="relative"
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      >
                        <img src={targetChainConfig.icon} alt={targetChainConfig.symbol} className="w-4 h-4" />
                      </motion.div>
                      <span className="text-sm font-medium text-white flex-1">{targetChainConfig.symbol}</span>
                      <motion.svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        strokeWidth={2} 
                        stroke="currentColor" 
                        className="w-3 h-3 text-slate-400 group-hover:text-blue-400 transition-colors"
                        whileHover={{ y: 1 }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                      </motion.svg>
                    </motion.button>
                  </div>
                  
                  {/* Received Amount */}
                  <div className="flex items-center justify-between mb-2 relative z-10">
                    <motion.input
                      type="text"
                      placeholder="0.00"
                      value={amount ? getEstimatedReceive().toFixed(6) : ''}
                      readOnly
                      className="flex-1 bg-transparent text-xl font-semibold text-white placeholder-slate-400 border-none outline-none cursor-default mr-3"
                      animate={{ opacity: amount ? 1 : 0.5 }}
                    />
                    <div className="flex items-center gap-1 bg-slate-700/50 rounded-lg px-2 py-2.5 border border-slate-600/50 min-w-[70px] max-w-[110px] flex-shrink-0 min-h-[44px]">
                      <img src={assetConfig.icon} alt={assetConfig.symbol} className="w-4 h-4" />
                      <span className="text-sm font-medium text-white flex-1">{assetConfig.symbol}</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-xs relative z-10">
                    <span className="text-slate-400">
                      Balance: {balances[targetChain][asset].toLocaleString()} {assetConfig.symbol}
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Enhanced Bridge Details */}
              <motion.div 
                className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50 relative group"
                variants={cardVariants}
                whileHover={{ scale: 1.01 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="space-y-2 relative z-10">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-400">Bridge Fee</span>
                    <span className="text-sm font-medium text-white">{getCurrentFee()} {assetConfig.symbol}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-400">Estimated Time</span>
                    <span className="text-sm font-medium text-white">~3 minutes</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-400">You'll Receive</span>
                    <motion.span 
                      className="text-sm font-medium text-white"
                      key={amount}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {amount ? getEstimatedReceive().toFixed(6) : '0.00'} {assetConfig.symbol}
                    </motion.span>
                  </div>
                </div>
              </motion.div>

              {/* Enhanced Bridge Button */}
              <motion.button 
                onClick={handleBridge}
                disabled={!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0}
                className={`w-full py-3 px-6 rounded-xl font-medium transition-all duration-300 text-sm relative overflow-hidden group ${
                  !amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0
                    ? 'bg-slate-700/50 text-slate-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 text-white'
                }`}
                whileHover={!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0 ? {} : { 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(59, 130, 246, 0.4)"
                }}
                whileTap={!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0 ? {} : { scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                {/* Animated background gradient */}
                {!(!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  />
                )}
                
                <span className="relative z-10">
                  {!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0 ? 'Enter Amount' : 'Bridge Tokens'}
                </span>
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Bridge History Section */}
      <motion.div 
        className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
        variants={cardVariants}
        whileHover={{ 
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        {/* Animated background pattern - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''}`}></div>

        <div className="relative z-10 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Bridge History</h2>
          <div className="space-y-3">
            {[
              { from: 'XRPL', to: 'Ethereum', asset: 'RLUSD', amount: '1,000', received: '999.5', time: '5 min ago', status: 'Completed' },
              { from: 'Ethereum', to: 'XRPL', asset: 'RLUSD', amount: '500', received: '499.98', time: '2 hours ago', status: 'Completed' },
              { from: 'XRPL', to: 'Arbitrum', asset: 'XRP', amount: '750', received: '749.93', time: '1 day ago', status: 'Completed' },
              { from: 'BNB', to: 'XRPL', asset: 'XRP', amount: '250', received: '249.99', time: '2 days ago', status: 'Completed' }
            ].map((bridge, i) => (
              <motion.div 
                key={i} 
                className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 hover:border-blue-500/40 transition-all duration-500"
                variants={cardVariants}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * i }}
                whileHover={{ 
                  scale: 1.01,
                  boxShadow: "0 0 20px rgba(59, 130, 246, 0.1)"
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <motion.div 
                      className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/40 flex items-center justify-center"
                      whileHover={{ scale: 1.1, rotate: 180 }}
                      transition={{ duration: 0.3 }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-blue-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                      </svg>
                    </motion.div>
                    <div>
                      <div className="font-medium text-white text-sm">
                        {bridge.from} → {bridge.to}
                      </div>
                      <div className="text-xs text-slate-300">
                        {bridge.amount} {bridge.asset} → {bridge.received} {bridge.asset} • {bridge.time}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <motion.div 
                      className="font-medium text-xs px-3 py-1 rounded-full bg-green-500/20 text-green-400 border border-green-500/30"
                      whileHover={{ scale: 1.05 }}
                    >
                      {bridge.status}
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Selection Modals */}
      <AnimatePresence>
        {showChainSelect === 'source' && (
          <SelectionModal 
            isOpen={showChainSelect === 'source'}
            onClose={() => setShowChainSelect(null)}
            onSelect={(chain) => setSourceChain(chain as ChainType)}
            options={direction === 'from-xrpl' ? ['XRPL'] : availableChains}
            title="Select Source Chain"
            type="chain"
          />
        )}

        {showChainSelect === 'target' && (
          <SelectionModal 
            isOpen={showChainSelect === 'target'}
            onClose={() => setShowChainSelect(null)}
            onSelect={(chain) => setTargetChain(chain as ChainType)}
            options={direction === 'from-xrpl' ? availableChains : ['XRPL']}
            title="Select Target Chain"
            type="chain"
          />
        )}

        {showAssetSelect && (
          <SelectionModal 
            isOpen={showAssetSelect}
            onClose={() => setShowAssetSelect(false)}
            onSelect={(asset) => setAsset(asset as AssetType)}
            options={['XRP', 'RLUSD']}
            title="Select Asset"
            type="token"
          />
        )}

        {/* Enhanced Confirmation Modal */}
        {showConfirmation && createPortal(
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-slate-900/80 backdrop-blur-xl flex items-center justify-center z-[9999] p-4">
            <motion.div 
              className="rounded-xl p-5 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 w-full max-w-sm"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
            >
                      {/* Animated background pattern - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''} rounded-xl`}></div>

              <div className="relative z-10">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Confirm Bridge</h3>
                  <button 
                    onClick={() => setShowConfirmation(false)}
                    className="p-1.5 rounded-lg hover:bg-slate-800/50 transition-colors text-slate-400 hover:text-white"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Bridge Summary */}
                <div className="bg-slate-800/50 rounded-xl p-4 mb-4 border border-slate-700/50 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-400">From</span>
                    <span className="text-sm font-medium text-white">{sourceChainConfig.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-400">To</span>
                    <span className="text-sm font-medium text-white">{targetChainConfig.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-400">Asset</span>
                    <span className="text-sm font-medium text-white">{assetConfig.symbol}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-400">Amount</span>
                    <span className="text-sm font-medium text-white">{amount} {assetConfig.symbol}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-400">Bridge Fee</span>
                    <span className="text-sm font-medium text-white">{getCurrentFee()} {assetConfig.symbol}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-400">You'll Receive</span>
                    <span className="text-sm font-medium text-white">{getEstimatedReceive().toFixed(6)} {assetConfig.symbol}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  <motion.button 
                    onClick={confirmBridge}
                    className="w-full bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 text-white font-medium py-3 px-4 rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 relative overflow-hidden group"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      animate={{ x: ['-100%', '100%'] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    />
                    <span className="relative z-10">Confirm Bridge</span>
                  </motion.button>
                  <button 
                    onClick={() => setShowConfirmation(false)}
                    className="w-full bg-slate-700/50 text-white font-medium py-3 px-4 rounded-xl hover:bg-slate-700/70 transition-all duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </div>, document.body
        )}
      </AnimatePresence>
    </div>
  );
};

export default Bridge; 